import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Shield, AlertCircle, CheckCircle2 } from 'lucide-react';
import { countryCodes } from '../mock';
import { validateForm, sanitizePhoneInput } from '../utils/validation';
import { useToast } from '../hooks/use-toast';
import { getGeolocation, submitLead, getDialCodeFromCountryCode } from '../services/apiService';

const RegistrationForm = ({ title = "Register Your Trademark for Only $45", subtitle = "Protect your brand with trusted expertise" }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    country: 'US',
    phone: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [geoLocationData, setGeoLocationData] = useState(null);

  // Fetch geolocation on component mount
  useEffect(() => {
    const fetchGeolocation = async () => {
      const geoData = await getGeolocation();
      setGeoLocationData(geoData);
      
      if (geoData.success && geoData.countryCode) {
        const dialCode = getDialCodeFromCountryCode(geoData.countryCode);
        setFormData(prev => ({
          ...prev,
          countryCode: dialCode,
          country: geoData.countryCode
        }));
      }
    };

    fetchGeolocation();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    let sanitizedValue = value;
    
    // Sanitize phone input - only allow digits
    if (name === 'phone') {
      sanitizedValue = sanitizePhoneInput(value);
      // Limit to 10 digits
      if (sanitizedValue.length > 10) {
        sanitizedValue = sanitizedValue.slice(0, 10);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCountryCodeChange = (value) => {
    // Extract country code from dial code
    const selectedCountry = countryCodes.find(c => c.code === value);
    setFormData(prev => ({
      ...prev,
      countryCode: value,
      country: selectedCountry?.country || 'US'
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const validationErrors = validateForm(formData);
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    
    // Prepare data for CRM API
    const leadData = {
      name: formData.name,
      email: formData.email,
      countryDialCode: formData.countryCode,
      country: formData.country,
      phoneNumber: formData.phone, // Only numeric
      description: formData.description
    };

    // Submit to CRM API via apiService
    const result = await submitLead(leadData, geoLocationData);

    console.log('Lead submission result:', result);

    setIsSubmitting(false);

    if (result.success) {
      console.log('Redirecting to:', result.redirectUrl);
      
      toast({
        title: "Success!",
        description: "Registration submitted successfully. Redirecting..."
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        countryCode: '+1',
        country: 'US',
        phone: '',
        description: ''
      });
      
      setIsSuccess(true);
      
      // Redirect to application page with customer ID
      setTimeout(() => {
        window.location.href = result.redirectUrl;
      }, 1500);
    } else {
      // Show error message
      console.error('Submission failed:', result.error);
      toast({
        title: "Error",
        description: result.error || "Failed to submit. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="bg-slate-900 rounded-xl shadow-2xl shadow-blue-500/30 p-8 max-w-md w-full border border-blue-500/20">
      <div className="flex justify-center mb-6">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
          <Shield className="w-8 h-8 text-blue-700" />
        </div>
      </div>
      
      <h3 className="text-2xl font-bold text-center mb-2">{title}</h3>
      <p className="text-gray-300 text-center mb-6">{subtitle}</p>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name Field */}
        <div>
          <Label htmlFor="name" className="flex items-center mb-2">
            <span>Name</span>
            <span className="text-red-500 ml-1">*</span>
          </Label>
          <Input
            id="name"
            name="name"
            type="text"
            placeholder="Enter your full name"
            value={formData.name}
            onChange={handleInputChange}
            className={errors.name ? 'border-red-500' : 'border-slate-600 focus:border-blue-400 bg-slate-800/50'}
          />
          {errors.name && (
            <p className="text-red-500 text-xs mt-1 flex items-center">
              <AlertCircle className="w-3 h-3 mr-1" />
              {errors.name}
            </p>
          )}
        </div>

        {/* Email Field */}
        <div>
          <Label htmlFor="email" className="flex items-center mb-2">
            <span>Email</span>
            <span className="text-red-500 ml-1">*</span>
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="Enter your email address"
            value={formData.email}
            onChange={handleInputChange}
            className={errors.email ? 'border-red-500' : 'border-slate-600 focus:border-blue-400 bg-slate-800/50'}
          />
          {errors.email && (
            <p className="text-red-500 text-xs mt-1 flex items-center">
              <AlertCircle className="w-3 h-3 mr-1" />
              {errors.email}
            </p>
          )}
        </div>

        {/* Phone Field */}
        <div>
          <Label htmlFor="phone" className="flex items-center mb-2">
            <span>Contact Number</span>
            <span className="text-red-500 ml-1">*</span>
          </Label>
          <div className="flex gap-2">
            <Select value={formData.countryCode} onValueChange={handleCountryCodeChange}>
              <SelectTrigger className="w-28">
                <SelectValue placeholder="🇺🇸 +1" />
              </SelectTrigger>
              <SelectContent>
                {countryCodes.map((country) => (
                  <SelectItem key={`${country.code}-${country.country}`} value={country.code}>
                    {country.flag} {country.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              id="phone"
              name="phone"
              type="tel"
              placeholder="1234567890 (10 digits)"
              value={formData.phone}
              onChange={handleInputChange}
              maxLength="10"
              inputMode="numeric"
              pattern="[0-9]*"
              className={`flex-1 ${errors.phone ? 'border-red-500' : 'border-slate-600 focus:border-blue-400 bg-slate-800/50'}`}
            />
          </div>
          {errors.phone && (
            <p className="text-red-500 text-xs mt-1 flex items-center">
              <AlertCircle className="w-3 h-3 mr-1" />
              {errors.phone}
            </p>
          )}
        </div>

        {/* Description Field */}
        <div>
          <Label htmlFor="description" className="flex items-center mb-2">
            <span>Description</span>
            <span className="text-red-500 ml-1">*</span>
          </Label>
          <Textarea
            id="description"
            name="description"
            placeholder="Tell us about your trademark needs..."
            value={formData.description}
            onChange={handleInputChange}
            className={`min-h-[100px] ${errors.description ? 'border-red-500' : 'border-slate-600 focus:border-blue-400 bg-slate-800/50'}`}
          />
          {errors.description && (
            <p className="text-red-500 text-xs mt-1 flex items-center">
              <AlertCircle className="w-3 h-3 mr-1" />
              {errors.description}
            </p>
          )}
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          className="w-full bg-gradient-to-r from-blue-700 to-blue-800 hover:from-amber-500 hover:to-amber-600 text-white font-semibold py-6 transition-all duration-300"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </span>
          ) : isSuccess ? (
            <span className="flex items-center justify-center">
              <CheckCircle2 className="mr-2 h-5 w-5" />
              Success!
            </span>
          ) : (
            'Start Your Registration'
          )}
        </Button>

        {/* Terms */}
        <p className="text-xs text-gray-500 text-center">
          By submitting, you agree to our{' '}
          <a href="/terms-conditions" className="text-blue-700 hover:underline">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="/privacy-policy" className="text-blue-700 hover:underline">
            Privacy Policy
          </a>
        </p>
      </form>
    </div>
  );
};

export default RegistrationForm;
